import {
  Box,
  Card,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { useCallback, useMemo, useState } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { useDispatch } from 'store';
import { getLoginLogs } from 'store/slices/logs';
import Loader from 'ui-component/Loader';
import styled from '@emotion/styled';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  boxShadow: theme.shadows[6],
  borderRadius: theme.spacing(1),
  height: '100%',
  overflowX: 'hidden',
}));

const LogsTable = () => {
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch all unique usernames (fetch with large limit to get all usernames)
  const fetchAllUsers = async () => {
    const response = await dispatch(
      getLoginLogs({
        page: 1,
        per_page: 10000, // Large limit to get all usernames
      })
    );
    return response.data;
  };

  const { data: allUsersData = {} } = useQuery({
    queryKey: ['allUsers'],
    queryFn: fetchAllUsers,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get unique usernames from all data
  const uniqueUsers = useMemo(() => {
    const users = new Set();
    const allLogs = allUsersData?.data || [];
    allLogs.forEach((log) => {
      if (log.actor || log.username) {
        users.add(log.actor || log.username);
      }
    });
    return Array.from(users).sort().map((user) => ({
      label: user,
      value: user,
    }));
  }, [allUsersData]);

  // Get unique actions from all data
  const uniqueActions = useMemo(() => {
    const actions = new Set();
    const allLogs = allUsersData?.data || [];
    allLogs.forEach((log) => {
      if (log.action) {
        actions.add(log.action);
      }
    });
    return Array.from(actions).sort().map((action) => ({
      label: action,
      value: action,
    }));
  }, [allUsersData]);

  // Get unique statuses from all data
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set();
    const allLogs = allUsersData?.data || [];
    allLogs.forEach((log) => {
      if (log.status) {
        statuses.add(String(log.status));
      }
    });
    return Array.from(statuses).sort().map((status) => ({
      label: status,
      value: status,
    }));
  }, [allUsersData]);

  // Fetch logs
  const fetchData = async () => {
    const params = {
      page: pagination.pageIndex + 1, // API uses 1-based pagination
      per_page: pagination.pageSize,
    };
    if (usernameFilter) params.username = usernameFilter;
    if (actionFilter) params.action = actionFilter;
    if (statusFilter) params.status = statusFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await dispatch(getLoginLogs(params));
    return response.data;
  };

  const {
    data: logsData = {},
    isFetching,
    isLoading,
  } = useQuery({
    queryKey: [
      'loginLogs',
      pagination.pageIndex,
      pagination.pageSize,
      usernameFilter,
      actionFilter,
      statusFilter,
      startDate,
      endDate,
    ],
    queryFn: fetchData,
    refetchOnWindowFocus: false,
  });

  const logs = logsData?.data || [];
  const total = logsData?.total || 0;

  // Filter change handlers
  const handleUsernameChange = useCallback((e) => {
    setUsernameFilter(e.target.value || '');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleActionChange = useCallback((e) => {
    setActionFilter(e.target.value || '');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleStatusChange = useCallback((e) => {
    setStatusFilter(e.target.value || '');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleStartDateChange = useCallback((e) => {
    setStartDate(e.target.value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleEndDateChange = useCallback((e) => {
    setEndDate(e.target.value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Table Columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'actor',
        header: 'Actor',
        size: 150,
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 150,
      },
      {
        accessorKey: 'target',
        header: 'Target',
        size: 150,
        Cell: ({ cell }) => cell.getValue() || '-',
      },
      {
        accessorKey: 'method',
        header: 'Method',
        size: 100,
      },
      {
        accessorKey: 'path',
        header: 'Path',
        size: 200,
        enableSorting: true,
        Cell: ({ cell }) => {
          const pathValue = cell.getValue() || '-';
          return (
            <Tooltip title={pathValue} arrow placement="top">
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                  cursor: 'help',
                }}
              >
                {pathValue}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'ip_address',
        header: 'IP Address',
        size: 150,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 100,
        Cell: ({ cell }) => {
          const status = cell.getValue();
          const color =
            status === '200' || status === '201'
              ? 'green'
              : status === '401' || status === '403'
              ? 'red'
              : 'orange';
          return (
            <span style={{ color, fontWeight: 500 }}>{status || '-'}</span>
          );
        },
      },
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        size: 200,
        Cell: ({ cell }) => {
          const timestamp = cell.getValue();
          if (!timestamp) return '-';
          try {
            const date = new Date(timestamp);
            return date.toLocaleString();
          } catch {
            return timestamp;
          }
        },
      },
    ],
    []
  );



  // Table Setup
  const table = useMaterialReactTable({
    columns,
    data: logs,
    getRowId: (row) => row._id || Math.random().toString(),
    manualPagination: true,
    rowCount: total,
    onPaginationChange: setPagination,
    state: {
      pagination,
      globalFilter,
      isLoading: isFetching,
    },
    onGlobalFilterChange: setGlobalFilter,
    enableGlobalFilter: true,
    enableColumnFilters: false,
    enableFilters: false,
    renderTopToolbarCustomActions: () => (
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ width: 180 }}>
            <Select
              value={usernameFilter || ''}
              onChange={handleUsernameChange}
            displayEmpty
            sx={{
              backgroundColor: 'background.paper',
            }}
          >
            <MenuItem value="">
              <em>Filter by Username</em>
            </MenuItem>
            {uniqueUsers.map((user) => (
              <MenuItem key={user.value} value={user.value}>
                {user.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 180 }}>
          <Select
            value={actionFilter || ''}
            onChange={handleActionChange}
            displayEmpty
            sx={{
              backgroundColor: 'background.paper',
            }}
          >
            <MenuItem value="">
              <em>Filter by Action</em>
            </MenuItem>
            {uniqueActions.map((action) => (
              <MenuItem key={action.value} value={action.value}>
                {action.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 180 }}>
          <Select
            value={statusFilter || ''}
            onChange={handleStatusChange}
            displayEmpty
            sx={{
              backgroundColor: 'background.paper',
            }}
          >
            <MenuItem value="">
              <em>Filter by Status</em>
            </MenuItem>
            {uniqueStatuses.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          value={startDate}
          onChange={handleStartDateChange}
          placeholder="Start Date"
          InputLabelProps={{ shrink: false }}
          sx={{
            width: 180,
            backgroundColor: 'background.paper',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
            },
          }}
        />
        <TextField
          size="small"
          type="date"
          value={endDate}
          onChange={handleEndDateChange}
          placeholder="End Date"
          InputLabelProps={{ shrink: false }}
          sx={{
            width: 180,
            backgroundColor: 'background.paper',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
            },
          }}
        />
      </Box>
    ),
  });

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        margin: '0 auto',
        padding: '2rem',
        borderRadius: '10px',
        overflowX: 'hidden',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box mb={3}>
        <Typography variant="f32">Login Logs</Typography>
      </Box>

      <StyledCard>
        <MaterialReactTable table={table} />
      </StyledCard>
    </Box>
  );
};

const queryClient = new QueryClient();

const Logs = () => (
  <Box>
    <QueryClientProvider client={queryClient}>
      <LogsTable />
    </QueryClientProvider>
  </Box>
);

export default Logs;
